import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { filter, map } from 'rxjs/operators';

import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  autopaySyncIconDark,
  documentIcon,
  dollarBillIcon,
  dollarIcon,
  propertyTaxIcon as propertyTaxIconUri,
  heroIconsDocuments,
  heroIconsDocumentsLime,
  heroIconsDocumentsWhite,
  heroIconsPayments,
  heroIconsPaymentsLime,
  heroIconsPaymentsWhite,
  heroIconsSettings,
  heroIconsSettingsLime,
  heroIconsSettingsWhite,
  houseIcon,
  messageIconDark,
  mobileIcon,
  navEnvelopeIconLime as navEnvelopeIconLimeUri,
  navEnvelopeIcon as navEnvelopeIconUri,
  navEnvelopeIconWhite as navEnvelopeIconWhiteUri,
  navHelpIconLime as navHelpIconLimeUri,
  navHelpIcon as navHelpIconUri,
  navHelpIconWhite as navHelpIconWhiteUri,
  phoneIcon,
  profileSettingsIcon,
  questionIcon,
  recycleSyncIcon,
  heroiconsWarning,
} from '@ld.consumer-portal/data-uri';
import { MORTGAGE_CUSTOMER_SERVICE_DISPLAY, PAYOFF_QUOTE_ERRORS } from '@ld.consumer-portal/constants';
import { PayoffQuoteService } from '@ld.consumer-portal/data-access';
import { ErrorModalComponent } from '@ld.consumer-portal/modals';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { DocumentsNavigationService, PaperlessNavigationService } from '@ld.consumer-portal/routing';
import { EnvironmentFeatureService, LoanSelectionService, PlatformService } from '@ld.consumer-portal/utils';
import { NativeMenuService } from '../native-bottom-sheet/native-menu.service';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

type LoanType = 'MORTGAGE' | 'HELOC' | 'HOME_EQUITY';

interface NavTabItem {
  id: string;
  labelKey: string;
  action: () => void;
  disabled?: boolean;
}

interface SettingsMenuItem {
  id: string;
  labelKey: string;
  icon: string;
  action: () => void;
}

@Component({
  selector: 'ld-servicing-header',
  imports: [MatDividerModule, MatIconModule, MatMenuModule, SvgIconComponent, TranslocoDirective, ErrorModalComponent],
  templateUrl: './servicing-header.component.html',
  styleUrls: ['./servicing-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'sticky top-[64px] z-40' },
})
export class ServicingHeaderComponent {
  private router = inject(Router);
  private loanSelectionService = inject(LoanSelectionService);
  private transloco = inject(TranslocoService);
  private env = inject(ENVIRONMENT);
  private platformService = inject(PlatformService);
  private breakpointObserver = inject(BreakpointObserver);
  private payoffQuoteService = inject(PayoffQuoteService);
  private envFeatureService = inject(EnvironmentFeatureService);
  protected readonly paperlessNavService = inject(PaperlessNavigationService);
  protected readonly documentsNavService = inject(DocumentsNavigationService);

  readonly isNativeMobile = this.platformService.isNativeMobile;
  protected readonly nativeMenuService = inject(NativeMenuService);

  private readonly isMobileViewport = toSignal(
    this.breakpointObserver.observe('(max-width: 767px)').pipe(map((result) => result.matches)),
    { initialValue: this.breakpointObserver.isMatched('(max-width: 767px)') },
  );
  readonly isMobile = computed(() => this.isNativeMobile() || this.isMobileViewport());

  private readonly currentUrl = signal(this.router.url);

  readonly showNavBar = computed(() => {
    const url = this.currentUrl();
    return (
      url.includes('/loans/') ||
      url.includes('/manage-paperless/') ||
      url.includes('/contact-information') ||
      url.includes('/help/') ||
      url.includes('/messages/')
    );
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
      });
  }

  readonly messageIconDark = messageIconDark;
  readonly documentIcon = documentIcon;
  readonly profileSettingsIcon = profileSettingsIcon;
  readonly recycleSyncIcon = recycleSyncIcon;
  readonly autopayIcon = autopaySyncIconDark;
  readonly faqIcon = questionIcon;
  readonly payoffQuoteIcon = dollarBillIcon;
  readonly propertyTaxIcon = propertyTaxIconUri;
  readonly homeownersInsuranceIcon = houseIcon;
  readonly insuranceClaimsIcon = documentIcon;
  readonly financialHardshipIcon = dollarIcon;
  readonly mobileAppTutorialIcon = mobileIcon;
  readonly contactUsIcon = phoneIcon;

  readonly navPaymentsIcon = heroIconsPayments;
  readonly navDocumentsIcon = heroIconsDocuments;
  readonly navSettingsIcon = heroIconsSettings;
  readonly navHelpIcon = navHelpIconUri;
  readonly navEnvelopeIcon = navEnvelopeIconUri;

  readonly navPaymentsIconWhite = heroIconsPaymentsWhite;
  readonly navDocumentsIconWhite = heroIconsDocumentsWhite;
  readonly navSettingsIconWhite = heroIconsSettingsWhite;
  readonly navHelpIconWhite = navHelpIconWhiteUri;
  readonly navEnvelopeIconWhite = navEnvelopeIconWhiteUri;

  readonly navPaymentsIconLime = heroIconsPaymentsLime;
  readonly navDocumentsIconLime = heroIconsDocumentsLime;
  readonly navSettingsIconLime = heroIconsSettingsLime;
  readonly navHelpIconLime = navHelpIconLimeUri;
  readonly navEnvelopeIconLime = navEnvelopeIconLimeUri;
  readonly hasLoan = computed(() => this.loanSelectionService.loanCount() > 0);

  protected readonly isHeloc = computed(() => this.loanSelectionService.selectedLoanType() === 'HELOC');

  protected readonly isPayoffQuoteDisabled = computed(() => {
    this.payoffQuoteService.eligibilityVersion();
    const isHeloc = this.isHeloc();
    const featureDisabled = !this.envFeatureService.isEnabled('PayoffQuoteRequest');
    const selectedLoan = this.loanSelectionService.selectedLoan();
    const loanNumber = selectedLoan?.loanNumber ?? '';
    const markedNotEligible = loanNumber ? this.payoffQuoteService.isMarkedNotEligible(loanNumber) : false;
    const alreadyRequested = loanNumber ? this.payoffQuoteService.hasAlreadyRequested(loanNumber) : false;
    return isHeloc || featureDisabled || markedNotEligible || alreadyRequested;
  });

  protected readonly showPayoffContactUsModal = signal(false);

  protected readonly payoffContactUsIconSrc = heroiconsWarning;

  protected readonly customerServicePhone = MORTGAGE_CUSTOMER_SERVICE_DISPLAY;

  protected readonly payoffContactUsMessage = computed(() =>
    this.transloco.translate(PAYOFF_QUOTE_ERRORS.NOT_ELIGIBLE, {
      customerServicePhone: this.customerServicePhone,
    })
  );

  readonly shouldHideMessages = computed(() => {
    const loan = this.loanSelectionService.selectedLoan();
    if (!loan) return false;
    return loan.isPaidInFull || loan.isTransferredToThirdParty || loan.isTransferredToCenlar || !loan.isActive;
  });

  readonly showTourOption = computed(() => this.loanSelectionService.hasMortgageOrHeloc());

  readonly navTabItems = computed<NavTabItem[]>(() => {
    const hasLoan = this.hasLoan();
    return [
      {
        id: 'payments',
        labelKey: 'commonPayments',
        action: () => this.onPayments(),
        disabled: !hasLoan,
      },
      {
        id: 'statements',
        labelKey: 'commonStatementsDocuments',
        action: () => this.onStatementsDocuments(),
        disabled: !hasLoan,
      },
    ];
  });

  readonly settingsMenuItems = computed<SettingsMenuItem[]>(() => {
    return [
      {
        id: 'paperless',
        labelKey: 'commonPaperlessSettings',
        icon: this.recycleSyncIcon,
        action: () => this.onPaperlessSettings(),
      },
      {
        id: 'autopay',
        labelKey: 'commonManageAutopay',
        icon: this.autopayIcon,
        action: () => this.onManageAutopay(),
      },
      {
        id: 'contact-info',
        labelKey: 'commonProfileSettings',
        icon: this.profileSettingsIcon,
        action: () => this.onContactInformation(),
      },
    ];
  });

  protected getAccounts() {
    return this.loanSelectionService.loanSelectorAccounts();
  }

  protected getEffectiveLoanType(): LoanType | null {
    return this.loanSelectionService.selectedLoanType();
  }

  protected getEffectiveLoanAddress(): string | null {
    return this.loanSelectionService.selectedLoanAddress();
  }

  protected getEffectiveSelectedAccountId(): string | null {
    return this.loanSelectionService.selectedLoanNumber();
  }

  protected getFullLoanNumber(): string {
    return this.loanSelectionService.selectedLoanNumber() ?? '';
  }

  protected getSelectedAccountLast4(): string {
    const loanNumber = this.loanSelectionService.selectedLoanNumber();
    return loanNumber ? loanNumber.slice(-4) : '';
  }

  getLoanTypeLabel(): string {
    const type = this.getEffectiveLoanType();
    if (type === 'MORTGAGE') return this.transloco.translate('pageHeaderLoanTypesMortgage');
    if (type === 'HELOC') return this.transloco.translate('commonHeloc');
    if (type === 'HOME_EQUITY') return this.transloco.translate('pageHeaderLoanTypesHomeEquity');
    return type ?? '';
  }

  getLoanTypeHeaderColorClass(): string {
    return 'text-ld-lime';
  }

  getLoanTypeColorClass(): string {
    return 'text-ld-primary';
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getLoanTypeColorClassFor(_type: LoanType): string {
    return 'text-ld-primary';
  }

  getLoanTypeLabelFor(type: LoanType): string {
    if (type === 'MORTGAGE') return this.transloco.translate('pageHeaderLoanTypesMortgage');
    if (type === 'HELOC') return this.transloco.translate('commonHeloc');
    if (type === 'HOME_EQUITY') return this.transloco.translate('pageHeaderLoanTypesHomeEquity');
    return type ?? '';
  }

  isActiveTab(tab: 'payments' | 'statements' | 'settings' | 'help' | 'messages'): boolean {
    const url = this.currentUrl();
    switch (tab) {
      case 'payments':
        return (
          url.includes('/details') ||
          url.includes('/autopay') ||
          url.includes('/payment') ||
          url.includes('/request-funds') ||
          url.includes('/payoff-quote') ||
          url.includes('/manage-accounts')
        );
      case 'statements':
        return url.includes('/documents');
      case 'settings':
        return url.includes('/manage-paperless') || url.includes('/contact-information');
      case 'help':
        return url.includes('/help/');
      case 'messages':
        return url.includes('/messages/');
      default:
        return false;
    }
  }

  onLoanSelect(accountId: string): void {
    this.loanSelectionService.navigateToAccount(accountId);
  }

  onMessageCenter(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/messages', selectedLoan]);
    } else {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/messages', firstLoan.loan.loanNumber]);
      }
    }
  }

  onPayments(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/loans', selectedLoan, 'details']);
    }
  }

  onStatementsDocuments(): void {
    this.documentsNavService.navigateToDocuments();
  }

  onContactInformation(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/contact-information', selectedLoan]);
    } else {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/contact-information', firstLoan.loan.loanNumber]);
      }
    }
  }

  onManageAutopay(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/loans', selectedLoan, 'autopay']);
    }
  }

  onPaperlessSettings(): void {
    this.paperlessNavService.navigateToPaperlessSettings();
  }

  onFaqs(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/help', selectedLoan, 'faqs']);
    } else {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/help', firstLoan.loan.loanNumber, 'faqs']);
      }
    }
  }

  onPayoffQuote(): void {
    const selectedLoanData = this.loanSelectionService.selectedLoan();
    const loanNumber = selectedLoanData?.loanNumber ?? this.loanSelectionService.selectedLoanNumber();
    if (!loanNumber) {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/loans', firstLoan.loan.loanNumber, 'payoff-quote']);
      }
      return;
    }

    if (selectedLoanData) {
      const eligibility = this.payoffQuoteService.checkEligibility(
        selectedLoanData.loanNumber,
        selectedLoanData.productType,
        selectedLoanData.canRequestPayoffQuote
      );
      if (!eligibility.canRequestPayoffQuote) {
        if (selectedLoanData.canRequestPayoffQuote === false) {
          this.payoffQuoteService.markLoanAsNotEligible(selectedLoanData.loanNumber);
        }
        this.showPayoffContactUsModal.set(true);
        return;
      }
    }

    this.router.navigate(['/loans', loanNumber, 'payoff-quote']);
  }

  onClosePayoffContactUsModal(): void {
    this.showPayoffContactUsModal.set(false);
  }

  onPropertyTaxes(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/help', selectedLoan, 'property-taxes']);
    } else {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/help', firstLoan.loan.loanNumber, 'property-taxes']);
      }
    }
  }

  onHomeownersInsurance(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/help', selectedLoan, 'homeowners-insurance']);
    } else {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/help', firstLoan.loan.loanNumber, 'homeowners-insurance']);
      }
    }
  }

  onInsuranceClaims(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/help', selectedLoan, 'insurance-claims']);
    } else {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/help', firstLoan.loan.loanNumber, 'insurance-claims']);
      }
    }
  }

  onFinancialHardship(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/help', selectedLoan, 'financial-hardship']);
    } else {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/help', firstLoan.loan.loanNumber, 'financial-hardship']);
      }
    }
  }

  onMobileAppTutorial(): void {
    const url = this.env.appSettings?.Mobile_App_Demo;
    if (url) {
      window.open(url, '_blank');
    }
  }

  onContactUs(): void {
    const selectedLoan = this.loanSelectionService.selectedLoanNumber();
    if (selectedLoan) {
      this.router.navigate(['/help', selectedLoan, 'contact-us']);
    } else {
      const firstLoan = this.loanSelectionService.allLoanResponses()[0];
      if (firstLoan) {
        this.router.navigate(['/help', firstLoan.loan.loanNumber, 'contact-us']);
      }
    }
  }
}
